import moment from 'moment';
import React, { useEffect, useLayoutEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { CastProvider } from './chromecast/cast-provider';
import Alert from './components/Alert';
import { CurrentEnv } from './components/current-env/current-env';
import { isDebug, isLocalhost } from './config';
import { ConfirmContextProvider } from './context/ConfirmContext';
import i18n from './i18n';
import { queryClient, QueryClientProvider } from './queries/client';
import { store } from './store';

export function Providers({ children }: { children: React.ReactNode }) {
  useCSSLayers();

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Provider store={store}>
          <I18nextProvider i18n={i18n}>
            <CastProvider>
              <MomentLocaleProvider>
                <Alert>
                  <ConfirmContextProvider>
                    {isLocalhost ? <CurrentEnv /> : null}
                    {children}
                  </ConfirmContextProvider>
                </Alert>
              </MomentLocaleProvider>
            </CastProvider>
          </I18nextProvider>
        </Provider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

function useCSSLayers() {
  useLayoutEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = '@layer reset, page, component;';
    const head = document.querySelector('head');

    if (!head) {
      console.error('No head element found in document');
      return;
    }

    head.insertBefore(style, head.firstChild);

    return () => {
      style.remove();
    };
  }, []);
}

const lanaugeMap = {
  no: 'nb',
  dk: 'da',
};

function MomentLocaleProvider({ children }: { children: JSX.Element }) {
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    const lang = lanaugeMap[language] || language;
    moment.locale(lang);
    if (isDebug) {
      console.debug(`Current language set to: %c${lang}`, 'color:yellow');
    }
  }, [language]);

  return children;
}
